import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from './cn.js';

export function Sheet({ open, onClose, title, children, forced = false, mobileFullscreen = false }) {
  const [render, setRender] = useState(open);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setRender(true);
      requestAnimationFrame(() => setVisible(true));
      document.body.style.overflow = 'hidden';
    } else {
      setVisible(false);
      const timer = setTimeout(() => {
        setRender(false);
        document.body.style.overflow = '';
      }, 300); // match transition duration
      return () => clearTimeout(timer);
    }
  }, [open]);

  if (!render) return null;

  return createPortal(
    <div className={cn("fixed inset-0 flex justify-end sm:items-center sm:justify-center", forced ? "z-[200]" : "z-[100]")}>
      {/* Backdrop */}
      <div
        className={cn(
          'absolute inset-0 bg-black/20 backdrop-blur-sm transition-opacity duration-300',
          visible ? 'opacity-100' : 'opacity-0'
        )}
        onClick={!forced ? onClose : undefined}
      />

      {/* Sheet / Modal */}
      <div
        className={cn(
          'relative w-full max-w-lg transform bg-white/95 dark:bg-gray-900 shadow-ptcm-soft transition-all duration-300 ease-in-out',
          'flex flex-col sm:max-h-[85vh] sm:rounded-[32px] sm:m-4 border border-ptcm-border/80 dark:border-gray-800',
          forced 
            ? 'h-[100svh] sm:h-auto rounded-none sm:rounded-[32px]' // Full screen on mobile if forced
            : mobileFullscreen
              ? 'h-[100svh] sm:h-auto rounded-none sm:rounded-[32px]'
              : 'h-[90svh] sm:h-auto rounded-t-[32px] sm:rounded-b-[32px]', // Standard sheet on mobile
          visible ? 'translate-y-0 sm:translate-y-0 sm:scale-100 opacity-100' : 'translate-y-full sm:translate-y-10 sm:scale-95 opacity-0'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-ptcm-border/70 dark:border-gray-800">
          <h2 className="text-lg font-semibold text-ptcm-ink dark:text-gray-100">{title}</h2>
          {!forced && (
            <button
              onClick={onClose}
              className="p-2 -mr-2 text-ptcm-muted hover:text-ptcm-ink dark:hover:text-gray-200 rounded-full hover:bg-ptcm-panel dark:hover:bg-gray-800 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 sm:p-7 custom-scrollbar">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
